import React, { useEffect, useState } from 'react';
import {
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  TextField,
  Checkbox,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import SearchIcon from '@material-ui/icons/Search';
import { useDispatch, useSelector } from 'react-redux';
import { cmoMembersList } from '../../../containers/actions/pharmaCo/purchaseOrderActions';

const icon = <CheckBoxOutlineBlankIcon fontSize='small' />;
const checkedIcon = <CheckBoxIcon fontSize='small' />;

const AssociateCMOs = ({
  selectedCMOs,
  handleChangeCMOList,
  errors,
  editPageFlag,
}) => {
  const [accessType, setAccessType] = useState('select');
  const [selectModeCMOs, setSelectModeCMOs] = useState(selectedCMOs); // preserve last select-mode selection
  const dispatch = useDispatch();

  // Get the full CMO list with plants
  let cmoListRaw = useSelector((state) =>
    state.pcmp.pharmaCo.purchaseOrder.cmoMemberWithId
      ? state.pcmp.pharmaCo.purchaseOrder.cmoMemberWithId
      : []
  );

  // 1. Transform CMOMembersWithId into a flat list of plant options
  const transformedCMOList = React.useMemo(() => {
    const list = cmoListRaw.reduce((acc, cmo) => {
      if (cmo.vendorPlants && cmo.vendorPlants.length > 0) {
        const plantOptions = cmo.vendorPlants.map((plant) => ({
          organization_name: cmo.organization_name,
          organization_id: cmo.organization_id,
          vendorMosymphonyId: cmo.vendorMosymphonyId,
          vendorPlant: plant,
        }));
        return [...acc, ...plantOptions];
      }
      return acc;
    }, []);
    return list;
  }, [cmoListRaw]);

  // 2. On mount, fetch CMO list
  useEffect(() => {
    let { collabType, ecosystemId, organizationName, roleName } = localStorage;
    dispatch(
      cmoMembersList({
        collabType: collabType,
        roleName: roleName,
        organizationName: organizationName,
        ecosystemId: ecosystemId,
      })
    );
  }, [dispatch]);

  // 3. On cmoListRaw change, if not editPageFlag, select all by default
  useEffect(() => {
    if (!editPageFlag) {
      setAccessType('all');
      handleChangeCMOList('selectedCMOs', transformedCMOList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transformedCMOList]);

  // When selectedCMOs changes in edit mode, update selectModeCMOs (for initial fetch and external updates)
  useEffect(() => {
    if (accessType === 'select') {
      setSelectModeCMOs(selectedCMOs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCMOs]);

  // 4. Handle radio selection
  const handleAccessChange = (event) => {
    const value = event.target.value;
    setAccessType(value);
    if (value === 'all') {
      setSelectModeCMOs(selectedCMOs); // Save current selection before switching
      handleChangeCMOList('selectedCMOs', transformedCMOList);
    } else {
      // Restore previous select-mode selection, or initial associations if first time
      handleChangeCMOList('selectedCMOs', selectModeCMOs || []);
    }
  };

  // 5. Handle selection in dropdown
  const handleSelectionChange = (event, value) => {
    setSelectModeCMOs(value);
    handleChangeCMOList('selectedCMOs', value);
  };

  // 6. For displaying 'Your selections', match selectedCMOs to transformedCMOList for display
  const selectedDisplayList = transformedCMOList.filter((itemA) =>
    selectedCMOs.some(
      (itemB) =>
        itemA.vendorPlant === itemB.vendorPlant &&
        itemA.organization_id === itemB.organization_id
    )
  );
  if (errors?.associateCMOs) {
    console.error("AssociateCMOs: error", errors.associateCMOs);
  }

  return (
    <Box>
      {/* Heading */}
      <Typography variant='h6' gutterBottom>
        Associate CMOs <span style={{ color: 'red' }}>*</span>
      </Typography>

      {/* Radio Buttons */}
      <FormControl component='fieldset' style={{ marginBottom: '1rem' }}>
        <RadioGroup value={accessType} onChange={handleAccessChange}>
          <FormControlLabel
            value='all'
            control={<Radio color='primary' />}
            label={`Access all CMOs (${transformedCMOList.length} Plants)`}
          />
          <FormControlLabel
            value='select'
            control={<Radio color='primary' />}
            label='Access select CMOs'
          />
        </RadioGroup>
      </FormControl>

      {/* Autocomplete with checkbox for multiple selections */}
      {accessType === 'select' && (
        <Autocomplete
          multiple
          id='checkboxes-tags-demo'
          options={transformedCMOList}
          disableCloseOnSelect
          value={selectedDisplayList}
          onChange={handleSelectionChange}
          getOptionLabel={(option) =>
            `${option.organization_name} - ${option.vendorPlant}`
          }
          renderOption={(option, { selected }) => (
            <React.Fragment>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {option.organization_name} - {option.vendorPlant}
            </React.Fragment>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              variant='outlined'
              InputProps={{
                ...params.InputProps,
                startAdornment: <SearchIcon />,
              }}
              placeholder='Start typing to search and select'
            />
          )}
        />
      )}
      {/* Display selected CMOs */}
      <Box mt={2}>
        <Typography variant='body1' style={{ marginBottom: '0.5rem' }}>
          Your selections
        </Typography>
        <Box
          style={{
            border: errors?.associateCMOs
              ? '1px solid #f44336'
              : '1px solid #ccc',
            borderRadius: '4px',
            padding: '1rem',
            maxHeight: '150px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {selectedDisplayList.length > 0 ? (
            selectedDisplayList.map((val, index) => (
              <Typography
                key={index}
                variant='body2'
                style={{ fontSize: '14px' }}
              >
                {val.organization_name} - {val.vendorPlant}
              </Typography>
            ))
          ) : (
            <Typography variant='body2' style={{ color: '#aaa' }}>
              No selections available.
            </Typography>
          )}
        </Box>
        {errors?.associateCMOs && (
          <Typography variant='caption' style={{ color: '#f44336' }}>
            {errors?.associateCMOs}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default AssociateCMOs;
